<script setup lang="ts">
import {
  ChangelogServices,
  SalesDataManagerServices,
} from '@appinternal/karyawan-api-services';
import { reactive, watch } from 'vue';

import { Option } from 'lib/components/dropdown/Dropdown.vue.d';
import FilterContainer from 'lib/components/filtercontainer/FilterContainer.vue';
import {
  FilterField,
  MultiSelectFilterField,
} from 'lib/components/filtercontainer/FilterContainer.vue.d';

import {
  BaseChangelogPageProps,
  ChangelogFilterRaw,
  ChangelogOptionBoolean,
} from './ChangelogPage.vue.d';
import { useToast } from '../../utils';

const props = defineProps<BaseChangelogPageProps & { tableName: string }>();

const toast = useToast();

const fields = ((): FilterField[] => {
  const tempFields: FilterField[] = [
    {
      label: 'Tanggal',
      type: 'calendar',
      field: 'createdAt',
      visible: !props.removedFilters?.includes('createdAt'),
    },
    {
      label: 'Aksi',
      type: 'multiselect',
      field: 'action',
      visible: !props.removedFilters?.includes('action'),
      fetchOptionFn: async (): Promise<Option[]> => {
        return await fetchOptions('actionOptions');
      },
    },
    {
      label: props.objectNameColumn ?? props.object,
      type: 'multiselect',
      field: 'objectName',
      visible:
        !props.additionalTemplateFilters?.length &&
        !props.removedFilters?.includes('objectName'),
      fetchOptionFn: async (): Promise<Option[]> => {
        return await fetchOptions('objectNameOptions');
      },
    },
    {
      label: 'Field',
      type: 'multiselect',
      field: 'field',
      visible:
        !/Testing/.test(props.object) &&
        !props.removedFilters?.includes('field'),
      fetchOptionFn: async (): Promise<Option[]> => {
        return await fetchOptions('fieldOptions');
      },
    },
    {
      label: 'Diubah Oleh',
      type: 'multiselect',
      field: 'modifiedBy',
      visible: !props.removedFilters?.includes('modifiedBy'),
      fetchOptionFn: async (): Promise<Option[]> => {
        return await fetchOptions('modifiedByOptions');
      },
    },
  ].filter((field) => field.visible) as FilterField[];

  if (props.additionalTemplateFilters?.length) {
    props.additionalTemplateFilters?.forEach((each) => {
      tempFields.splice(each.index, 0, {
        ...each.filter,
        ...(each.filter.type === 'multiselect'
          ? {
              fetchOptionFn: async (): Promise<Option[]> => {
                const filter = each.filter as MultiSelectFilterField;
                return await fetchOptions(
                  filter.field as keyof ChangelogOptionBoolean,
                );
              },
            }
          : {}),
      });
    });
  }

  return tempFields;
})();

const filter = reactive<ChangelogFilterRaw>({});

/**
 * Fetches options for a specific filter.
 * If the filter options do not exist, it makes a request to get them.
 * It also handles the loading state for the options.
 *
 * @function fetchOptions
 * @param field
 * @returns {Promise<void>}
 */
const fetchOptions = async (
  field: keyof ChangelogOptionBoolean,
): Promise<Option[]> => {
  try {
    const { data } = await (
      props.isEmployee
        ? ChangelogServices.getChangelogOptions
        : SalesDataManagerServices.getLogOptions
    )({
      [field]: true,
      ...props.defaultParamsQuery,
      ...props.customParams,
    });

    return data.data[field] ?? [];
  } catch (error) {
    toast.add({
      error,
      message: 'Error, failed to fetch options.',
    });

    return [];
  }
};

/**
 * Clears all keys from the filter object.
 *
 * @function clearFilter
 * @returns {void}
 */
const clearFilter = (): void => {
  const keys = Object.keys(filter) as Array<keyof ChangelogFilterRaw>;
  keys.forEach((key) => delete filter[key]);
};

/**
 * Watches the filter object for changes.
 * If a key's value is an empty array, it removes that key from the filter object.
 *
 * @function watch
 * @returns {void}
 */
watch(filter, () => {
  const keys = Object.keys(filter) as Array<keyof ChangelogFilterRaw>;
  keys.forEach((key) => !filter[key]?.length && delete filter[key]);
});
</script>

<template>
  <FilterContainer
    :fields="fields"
    :fields-per-row="4"
    :table-name="props.tableName"
    @clear="clearFilter"
  />
</template>
